import { getAllJobs } from "./jobStorage";
import {
  queueCloudRecordDelete,
  queueCloudRecordSave,
} from "./workspaceCloudSync";

const STORE_PREFIX = "manucalc_shop_records_v1";

export const SHOP_RECORD_TYPES = {
  tools: {
    title: "Tool Library",
    singular: "Tool",
    storageKey: `${STORE_PREFIX}_tools`,
    route: "/tools",
    description: "Track cutters, drills, taps, inserts, holders, and shop tooling with feed/speed defaults and storage notes.",
    fields: [
      ["name", "Tool name", "text", true],
      ["toolType", "Tool type", "select", true, ["Endmill", "Drill", "Tap", "Reamer", "Insert", "Indexable holder", "Lathe tool", "Face mill", "Boring bar", "Chamfer tool", "Countersink", "Specialty tool"]],
      ["diameter", "Diameter", "number"],
      ["fluteCount", "Flute count", "number"],
      ["length", "Overall/cut length", "number"],
      ["stickout", "Stickout", "number"],
      ["toolMaterial", "Tool material", "select", false, ["HSS", "Cobalt", "Solid carbide", "Indexable carbide", "Brazed carbide", "Ceramic", "CBN", "PCD"]],
      ["coating", "Coating / grade", "text"],
      ["insertStyle", "Insert style", "text"],
      ["noseRadius", "Nose radius", "number"],
      ["edgeCount", "Edge count", "number"],
      ["manufacturer", "Manufacturer", "text"],
      ["partNumber", "Part number", "text"],
      ["preferredMaterials", "Preferred materials", "text"],
      ["defaultFeedsSpeeds", "User default feeds/speeds", "textarea"],
      ["toolCost", "Tool cost", "number"],
      ["toolLifeEstimate", "Tool life estimate", "text"],
      ["location", "Location/storage", "text"],
      ["status", "Status", "select", false, ["Active", "Inactive", "Needs review"]],
      ["notes", "Notes", "textarea"],
    ],
  },
  fixtures: {
    title: "Fixture Library",
    singular: "Fixture",
    storageKey: `${STORE_PREFIX}_fixtures`,
    route: "/fixtures",
    description: "Store vises, jaws, chucks, collets, clamps, welding fixtures, and inspection fixtures.",
    fields: [
      ["name", "Fixture name", "text", true],
      ["fixtureType", "Fixture type", "select", true, ["Vise", "Soft jaws", "Chuck", "Collet", "Angle plate", "Clamp", "Vacuum fixture", "Welding fixture", "Inspection fixture", "Custom fixture"]],
      ["machineCompatibility", "Machine compatibility", "text"],
      ["setupNotes", "Setup notes", "textarea"],
      ["workholdingNotes", "Workholding notes", "textarea"],
      ["relatedJobs", "Related jobs/plans", "text"],
      ["location", "Location", "text"],
      ["safetyNotes", "Safety notes", "textarea"],
      ["photoNotes", "Photo/storage notes", "textarea"],
      ["status", "Status", "select", false, ["Active", "Inactive", "Needs repair"]],
    ],
  },
  machines: {
    title: "Machine Database",
    singular: "Machine",
    storageKey: `${STORE_PREFIX}_machines`,
    route: "/machines",
    description: "Track machine capabilities so plans, feeds/speeds, setup sheets, and capacity checks use shop-specific limits.",
    fields: [
      ["name", "Machine name", "text", true],
      ["machineType", "Machine type", "select", true, ["CNC mill", "CNC lathe", "Manual mill", "Manual lathe", "Drill press", "Press brake", "Welder", "Saw", "Inspection", "Additive", "Other"]],
      ["manufacturer", "Manufacturer", "text"],
      ["model", "Model", "text"],
      ["workEnvelope", "Work envelope/travel", "text"],
      ["spindleRpm", "Max spindle RPM", "number"],
      ["horsepower", "Horsepower", "number"],
      ["toolCapacity", "Tool capacity", "number"],
      ["axisCount", "Axis count", "number"],
      ["controller", "Controller", "text"],
      ["coolantCapability", "Coolant capability", "text"],
      ["supportedOperations", "Supported operations", "textarea"],
      ["setupLimitations", "Setup limitations", "textarea"],
      ["maintenanceNotes", "Maintenance notes", "textarea"],
      ["availableHoursWeek", "Available hours/week", "number"],
      ["notes", "Notes", "textarea"],
      ["status", "Status", "select", false, ["Active", "Down", "Maintenance", "Inactive"]],
    ],
  },
  setupSheets: {
    title: "Setup Sheets",
    singular: "Setup Sheet",
    storageKey: `${STORE_PREFIX}_setupSheets`,
    route: "/setup-sheets",
    description: "Create mobile-friendly setup sheets from manufacturing plans, tools, fixtures, and inspection checkpoints.",
    fields: [
      ["planId", "Manufacturing plan", "job"],
      ["partName", "Part name", "text", true],
      ["jobName", "Job/plan name", "text"],
      ["material", "Material", "text"],
      ["machine", "Machine", "text"],
      ["operationNumber", "Operation number", "text"],
      ["tools", "Tools", "textarea"],
      ["fixtures", "Fixtures/workholding", "textarea"],
      ["workOffset", "Work offset", "text"],
      ["programName", "Program name", "text"],
      ["setupNotes", "Setup notes", "textarea"],
      ["safetyNotes", "Safety notes", "textarea"],
      ["speedsFeeds", "Speeds/feeds", "textarea"],
      ["inspectionCheckpoints", "Inspection checkpoints", "textarea"],
      ["revision", "Revision", "text"],
      ["operatorSignoff", "Operator signoff", "text"],
      ["photoNotes", "Setup photo notes", "textarea"],
    ],
  },
  jobTravelers: {
    title: "Job Travelers",
    singular: "Traveler",
    storageKey: `${STORE_PREFIX}_jobTravelers`,
    route: "/job-travelers",
    description: "Release and track jobs through operation sequence, signoffs, inspection, and status.",
    fields: [
      ["planId", "Manufacturing plan", "job"],
      ["jobName", "Job name", "text", true],
      ["partNumber", "Part number", "text"],
      ["revision", "Revision", "text"],
      ["quantity", "Quantity", "number"],
      ["customerReference", "Customer/internal reference", "text"],
      ["material", "Material", "text"],
      ["operationSequence", "Operation sequence", "textarea"],
      ["machineRoute", "Machine route", "textarea"],
      ["setupSheetLinks", "Setup sheet links", "textarea"],
      ["inspectionCheckpoints", "Inspection checkpoints", "textarea"],
      ["signoffSections", "Signoff sections", "textarea"],
      ["dueDate", "Due date", "date"],
      ["status", "Status", "select", false, ["Planned", "Released", "In Process", "Waiting", "Inspection", "Complete", "Hold"]],
      ["notes", "Notes", "textarea"],
    ],
  },
  inspectionSheets: {
    title: "Inspection Sheets",
    singular: "Inspection Sheet",
    storageKey: `${STORE_PREFIX}_inspectionSheets`,
    route: "/inspection",
    description: "Capture dimensional checks, method, gauge, actual measurements, pass/fail, and inspection records.",
    fields: [
      ["planId", "Manufacturing plan", "job"],
      ["partName", "Part name", "text", true],
      ["drawingRevision", "Drawing/revision", "text"],
      ["feature", "Feature/dimension", "text", true],
      ["nominal", "Nominal value", "number"],
      ["upperTolerance", "Upper tolerance", "number"],
      ["lowerTolerance", "Lower tolerance", "number"],
      ["measurementMethod", "Measurement method", "text"],
      ["gaugeUsed", "Gauge/tool used", "text"],
      ["actualMeasurement", "Actual measurement", "number"],
      ["passFail", "Pass/fail", "select", false, ["Not checked", "Pass", "Fail", "Review"]],
      ["inspector", "Inspector", "text"],
      ["inspectionDate", "Date", "date"],
      ["notes", "Notes", "textarea"],
    ],
  },
  shopBrainNotes: {
    title: "Shop Brain",
    singular: "Knowledge Note",
    storageKey: `${STORE_PREFIX}_shopBrainNotes`,
    route: "/shop-brain",
    description: "Capture lessons learned, setup notes, material notes, machine notes, tooling notes, and tribal knowledge.",
    fields: [
      ["title", "Title", "text", true],
      ["noteType", "Note type", "select", true, ["Lesson learned", "Shop tip", "Troubleshooting", "Setup note", "Material note", "Machine note", "Tooling note", "Customer/job note"]],
      ["tags", "Tags", "text"],
      ["linkedRecord", "Linked plan/tool/machine/SOP", "text"],
      ["body", "Knowledge note", "textarea", true],
      ["visibility", "Visibility", "select", false, ["Personal", "Company"]],
    ],
  },
};

const safeParse = (value, fallback = []) => {
  try {
    const parsed = JSON.parse(value || "[]");
    return Array.isArray(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
};

export const createRecordId = (prefix = "record") =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const getRecordConfig = (type) => SHOP_RECORD_TYPES[type];

export const getRecords = (type) => {
  const config = getRecordConfig(type);
  if (!config || typeof window === "undefined") return [];
  return safeParse(window.localStorage.getItem(config.storageKey), [])
    .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
};

export const saveRecords = (type, records) => {
  const config = getRecordConfig(type);
  if (!config || typeof window === "undefined") return [];
  window.localStorage.setItem(config.storageKey, JSON.stringify(records));
  window.dispatchEvent(new CustomEvent("manucalc:shop-records-updated", { detail: { type } }));
  return records;
};

export const getShopRecordTypes = () => Object.keys(SHOP_RECORD_TYPES);

export const mergeCloudRecords = (type, records) => saveRecords(type, records);

export const saveRecord = (type, record, user = null, accountProfile = null) => {
  const records = getRecords(type);
  const existing = records.find((item) => item.id === record.id);
  const now = new Date().toISOString();
  const workspaceCompanyId = accountProfile?.companyId || accountProfile?.companyOwnerUid || "";
  const nextRecord = {
    ...record,
    id: record.id || createRecordId(type),
    ownerId: record.ownerId || user?.uid || "local-user",
    companyId: record.companyId || workspaceCompanyId,
    workspaceScope: workspaceCompanyId ? "company" : "user",
    createdBy: record.createdBy || user?.uid || "local-user",
    updatedBy: user?.uid || "local-user",
    createdAt: existing?.createdAt || record.createdAt || now,
    updatedAt: now,
  };

  const next = existing
    ? records.map((item) => (item.id === nextRecord.id ? nextRecord : item))
    : [nextRecord, ...records];
  saveRecords(type, next);
  queueCloudRecordSave(type, nextRecord);
  return nextRecord;
};

export const deleteRecord = (type, id) => {
  saveRecords(type, getRecords(type).filter((item) => item.id !== id));
  queueCloudRecordDelete(type, id);
};

export const getShopStats = () => {
  const jobs = getAllJobs();
  const travelers = getRecords("jobTravelers");
  const setupSheets = getRecords("setupSheets");
  const inspections = getRecords("inspectionSheets");
  const machines = getRecords("machines");
  const tools = getRecords("tools");
  const fixtures = getRecords("fixtures");
  const notes = getRecords("shopBrainNotes");
  const estimatedHours = jobs.reduce(
    (sum, job) =>
      sum + (job.operations || []).reduce((opSum, op) => opSum + Number(op.setupHours || 0) + Number(op.runHours || 0), 0),
    0
  );

  return {
    activePlans: jobs.length,
    openTravelers: travelers.filter((item) => !["Complete", "Hold"].includes(item.status)).length,
    setupSheets: setupSheets.length,
    inspectionSheets: inspections.length,
    machinesLoaded: machines.filter((machine) => machine.status !== "Inactive").length,
    tools: tools.length,
    fixtures: fixtures.length,
    shopBrainNotes: notes.length,
    estimatedShopHours: Math.round(estimatedHours * 10) / 10,
  };
};

export const buildRecordFromPlan = (type, job) => {
  if (!job) return {};
  const firstPart = job.parts?.[0] || {};
  const operationsText = (job.operations || [])
    .map((operation, index) => `${index + 1}. ${operation.name || operation.operationTypeId || "Operation"} - ${operation.workPerformed || ""}`)
    .join("\n");
  const machineRoute = (job.machineRoute || []).join(", ");

  if (type === "setupSheets") {
    const firstOperation = job.operations?.[0] || {};
    return {
      planId: job.id,
      partName: firstPart.name || job.name || "",
      jobName: job.name || "",
      material: job.materialId || firstPart.materialId || "",
      machine: firstOperation.machineId || machineRoute,
      operationNumber: firstOperation.setupNumber || "1",
      tools: firstOperation.toolsUsed || firstOperation.toolDescription || "",
      fixtures: firstOperation.workholding || "",
      setupNotes: firstOperation.notes || job.notes || "",
      inspectionCheckpoints: firstOperation.inspectionCriteria || "",
      speedsFeeds: `Tool diameter: ${firstOperation.toolDiameter || ""}; Stock diameter: ${firstOperation.stockDiameter || ""}`,
      revision: job.revision || "A",
    };
  }

  if (type === "jobTravelers") {
    return {
      planId: job.id,
      jobName: job.name || "",
      partNumber: job.partNumber || firstPart.partNumber || "",
      revision: job.revision || "",
      quantity: job.quantity || firstPart.quantity || 1,
      customerReference: job.customer || "",
      material: job.materialId || firstPart.materialId || "",
      operationSequence: operationsText,
      machineRoute,
      inspectionCheckpoints: (job.operations || []).map((op) => op.inspectionCriteria).filter(Boolean).join("\n"),
      dueDate: job.dueDate || "",
      status: "Planned",
      notes: job.notes || "",
    };
  }

  if (type === "inspectionSheets") {
    return {
      planId: job.id,
      partName: firstPart.name || job.name || "",
      drawingRevision: job.revision || "",
      feature: "Critical feature from plan",
      measurementMethod: "Enter inspection method",
      passFail: "Not checked",
      inspectionDate: new Date().toISOString().slice(0, 10),
      notes: job.notes || "",
    };
  }

  return {};
};
